import { captureException } from '@sentry/react';
import {
  TCellValue,
  TColumnDefinition,
} from 'FeatureModules/DataImport/Components/DataImportTable/excelTable.types';
import { TOriginDatasetTableRowData } from 'FeatureModules/DataImport/Components/Forms/DatasetValidation';
import pick from 'lodash/pick';
import {
  IOriginTableSettings,
  TColumnKeys,
} from 'FeatureModules/DataImport/types/dataImport.types';
import {
  TPlotGeometryDatasetColumn,
  EDatasetColumn,
  DatasetCell,
  DatasetRow,
  TFarmSizeDatasetColumn,
  TDatasetColumnType,
  TPlotSizeDatasetColumn,
  EDatasetIssueSeverityLevel,
} from 'FeatureModules/DataImport/types/dataset.types';
import { v4 as uuid } from 'uuid';
import { originTableSettingsSchema } from '../schemas/dataImport.schemas';

export const DatasetColumnKeys: TDatasetColumnType[] = Object.values(EDatasetColumn);

type TDatasetColumnWithSampleData =
  | TPlotGeometryDatasetColumn
  | TPlotSizeDatasetColumn
  | TFarmSizeDatasetColumn
  | `${EDatasetColumn.GEOMETRY_WKT}`;

export const DatasetColumnSampleData: Record<TDatasetColumnWithSampleData, string> = {
  [EDatasetColumn.GEOMETRY_WKT]:
    'POLYGON (( 52.508749 13.462562, 52.508749 13.462562 , 52.508749 13.462562, 52.508749 13.462562 ))',
  [EDatasetColumn.PLOT_SIZE_HA]: '1.0 ha',
  [EDatasetColumn.PLOT_SIZE_KM2]: '1.0 km²',
  [EDatasetColumn.FARM_SIZE_HA]: '1.0 ha',
  [EDatasetColumn.FARM_SIZE_KM2]: '1.0 km²',
};

export const DatasetColumnName: Record<TDatasetColumnType, string> = {
  [EDatasetColumn.ORIGIN_CLUSTER_ID]: 'Origin cluster ID',
  [EDatasetColumn.FARM_ID]: 'Farm ID',
  [EDatasetColumn.PLOT_ID]: 'Plot ID',
  [EDatasetColumn.PLOT_NAME]: 'Plot name',
  [EDatasetColumn.PLOT_SIZE_KM2]: 'Plot size (Square km)',
  [EDatasetColumn.PLOT_SIZE_HA]: 'Plot size (Hectare)',
  [EDatasetColumn.FARM_NAME]: 'Farm name',
  [EDatasetColumn.FARM_OWNER_FULL_NAME]: 'Farm owner - Full name',
  [EDatasetColumn.FARM_OWNER_FIRST_NAME]: 'Farm owner - First name',
  [EDatasetColumn.FARM_OWNER_LAST_NAME]: 'Farm owner - Last name',
  [EDatasetColumn.NUMBER_OF_FARMERS]: 'Number of farmers',
  [EDatasetColumn.GEOMETRY_WKT]: 'Plot geometry (WKT)',
  [EDatasetColumn.FARM_SIZE_HA]: 'Farm size (Hectare)',
  [EDatasetColumn.FARM_SIZE_KM2]: 'Farm size (Square km)',
  [EDatasetColumn.ADDITIONAL_CROPS]: 'Additional crops',
  [EDatasetColumn.LAND_OWNERSHIP]: 'Land ownership',
  [EDatasetColumn.SITE_DESCRIPTION]: 'Site description',
  [EDatasetColumn.LATITUDE]: 'Plot coordinate latitude',
  [EDatasetColumn.LONGITUDE]: 'Plot coordinate longitude',
  [EDatasetColumn.COORDINATES_LAT_LNG]: 'Plot coordinates (Lat, Lng)',
  [EDatasetColumn.COORDINATES_LNG_LAT]: 'Plot coordinates (Lng, Lat)',
};

export const CoordinateDatasetColumnName: Record<TPlotGeometryDatasetColumn, string> = pick(
  DatasetColumnName,
  [EDatasetColumn.GEOMETRY_WKT]
);

const transformRowColumnData = <Value extends TCellValue>(
  cell: DatasetCell | undefined,
  columnKey: TColumnKeys,
  settings?: IOriginTableSettings
): object | TColumnDefinition<Value> => {
  if (!cell) {
    if (settings?.columns[columnKey]?.enabled) {
      return { [columnKey]: { value: '' } };
    }
    return {};
  }

  if (cell.issues?.some(issue => issue.severityLevel === EDatasetIssueSeverityLevel.ERROR)) {
    return {
      [columnKey]: {
        value: cell.value as string,
        isValid: false,
        errorMessage: cell.issues?.map(issue => issue.issueMessage).join('. '),
      },
    };
  } else if (
    cell.issues?.some(issue => issue.severityLevel === EDatasetIssueSeverityLevel.WARNING)
  ) {
    return {
      [columnKey]: {
        value: cell.value as string,
        isValid: true,
        warningMessage: cell.issues?.map(issue => issue.issueMessage).join('. '),
      },
    };
  }
  return {
    [columnKey]: {
      value: cell.value as string,
      isValid: true,
      infoMessage: cell.issues?.map(issue => issue.issueMessage).join('. '),
    },
  };
};

export const transformDataForDisplay = (
  row: DatasetRow,
  settings?: IOriginTableSettings
): TOriginDatasetTableRowData => {
  return {
    id: uuid(),
    // Required columns
    geometry: {
      value: row.geometryWkt.value as string,
      ...(row.geometryWkt.issues?.some(
        issue => issue.severityLevel === EDatasetIssueSeverityLevel.ERROR
      )
        ? {
            isValid: false,
            errorMessage: row.geometryWkt.issues?.map(issue => issue.issueMessage).join('. '),
          }
        : row.geometryWkt.issues?.some(
            issue => issue.severityLevel === EDatasetIssueSeverityLevel.WARNING
          )
        ? {
            isValid: true,
            warningMessage: row.geometryWkt.issues?.map(issue => issue.issueMessage).join('. '),
          }
        : {
            isValid: true,
            infoMessage: row.geometryWkt.issues?.map(issue => issue.issueMessage).join('. '),
          }),
    },
    plotId: {
      value: row.plotId.value as string,
      ...(row.plotId.issues?.some(issue => issue.severityLevel === EDatasetIssueSeverityLevel.ERROR)
        ? {
            isValid: false,
            errorMessage: row.plotId.issues?.map(issue => issue.issueMessage).join('. '),
          }
        : row.plotId.issues?.some(
            issue => issue.severityLevel === EDatasetIssueSeverityLevel.WARNING
          )
        ? {
            isValid: true,
            warningMessage: row.plotId.issues?.map(issue => issue.issueMessage).join('. '),
          }
        : {
            isValid: true,
            infoMessage: row.plotId.issues?.map(issue => issue.issueMessage).join('. '),
          }),
    },
    // Extended columns
    ...transformRowColumnData<string>(row.originClusterId, 'originClusterId', settings),
    ...transformRowColumnData<number>(row.farmId, 'farmId', settings),
    ...transformRowColumnData<number>(row.farmSizeHa, 'farmSize', settings),
    ...transformRowColumnData<string>(row.farmName, 'farmName', settings),
    ...transformRowColumnData<string>(row.plotName, 'plotName', settings),
    ...transformRowColumnData<number>(row.plotSizeHa, 'plotSize', settings),
    ...transformRowColumnData<string>(row.additionalCrops, 'additionalCrops', settings),
    ...transformRowColumnData<string>(row.farmOwnerFullName, 'farmOwnerFullName', settings),
    ...transformRowColumnData<string>(row.farmOwnerFirstName, 'farmOwnerFirstName', settings),
    ...transformRowColumnData<string>(row.farmOwnerLastName, 'farmOwnerLastName', settings),
    ...transformRowColumnData<number>(row.numberOfFarmers, 'numberOfFarmers', settings),
    ...transformRowColumnData<string>(row.landOwnership, 'landOwnership', settings),
    ...transformRowColumnData<string>(row.siteDescription, 'siteDescription', settings),
  };
};

export const transformColumnNamesToTableSettingColumns: (
  names: TDatasetColumnType[]
) => IOriginTableSettings['columns'] = names => {
  return names.reduce(
    (prev, columnName) => {
      switch (columnName) {
        case EDatasetColumn.ORIGIN_CLUSTER_ID:
          return { ...prev, originClusterId: { enabled: true } };
        case EDatasetColumn.FARM_ID:
          return { ...prev, farmId: { enabled: true } };
        case EDatasetColumn.FARM_NAME:
          return { ...prev, farmName: { enabled: true } };
        case EDatasetColumn.PLOT_ID:
          return { ...prev, plotId: { enabled: true } };
        case EDatasetColumn.PLOT_NAME:
          return { ...prev, plotName: { enabled: true } };
        case EDatasetColumn.FARM_OWNER_FULL_NAME:
          return { ...prev, farmOwnerFullName: { enabled: true } };
        case EDatasetColumn.FARM_OWNER_FIRST_NAME:
          return { ...prev, farmOwnerFirstName: { enabled: true } };
        case EDatasetColumn.FARM_OWNER_LAST_NAME:
          return { ...prev, farmOwnerLastName: { enabled: true } };
        case EDatasetColumn.NUMBER_OF_FARMERS:
          return { ...prev, numberOfFarmers: { enabled: true } };
        case EDatasetColumn.GEOMETRY_WKT:
          return {
            ...prev,
            geometry: { enabled: true, format: columnName },
          };
        case EDatasetColumn.PLOT_SIZE_KM2:
        case EDatasetColumn.PLOT_SIZE_HA:
          return {
            ...prev,
            plotSize: { enabled: true, format: columnName as TPlotSizeDatasetColumn },
          };
        case EDatasetColumn.FARM_SIZE_HA:
        case EDatasetColumn.FARM_SIZE_KM2:
          return {
            ...prev,
            farmSize: { enabled: true, format: columnName as TFarmSizeDatasetColumn },
          };
        case EDatasetColumn.ADDITIONAL_CROPS:
          return { ...prev, additionalCrops: { enabled: true } };
        case EDatasetColumn.LAND_OWNERSHIP:
          return { ...prev, landOwnership: { enabled: true } };
        case EDatasetColumn.SITE_DESCRIPTION:
          return { ...prev, siteDescription: { enabled: true } };
        // Columns are only supported in mapping and not in the table
        case EDatasetColumn.COORDINATES_LAT_LNG:
        case EDatasetColumn.COORDINATES_LNG_LAT:
        case EDatasetColumn.LATITUDE:
        case EDatasetColumn.LONGITUDE:
        default:
          throw new Error(`Unknown column key: ${columnName}`);
      }
    },
    {
      ...originTableSettingsSchema.default().columns,
    } as IOriginTableSettings['columns']
  );
};

export const matchColumnMappingToColumnKey = (column: TDatasetColumnType): string => {
  switch (column) {
    case EDatasetColumn.ORIGIN_CLUSTER_ID:
      return 'originClusterId';
    case EDatasetColumn.FARM_ID:
      return 'farmId';
    case EDatasetColumn.PLOT_ID:
      return 'plotId';
    case EDatasetColumn.PLOT_NAME:
      return 'plotName';
    case EDatasetColumn.GEOMETRY_WKT:
      return 'geometry';
    case EDatasetColumn.PLOT_SIZE_KM2:
    case EDatasetColumn.PLOT_SIZE_HA:
      return 'plotSize';
    case EDatasetColumn.FARM_SIZE_KM2:
    case EDatasetColumn.FARM_SIZE_HA:
      return 'farmSize';
    case EDatasetColumn.FARM_NAME:
      return 'farmName';
    case EDatasetColumn.FARM_OWNER_FULL_NAME:
      return 'farmOwnerFullName';
    case EDatasetColumn.FARM_OWNER_FIRST_NAME:
      return 'farmOwnerFirstName';
    case EDatasetColumn.FARM_OWNER_LAST_NAME:
      return 'farmOwnerLastName';
    case EDatasetColumn.NUMBER_OF_FARMERS:
      return 'numberOfFarmers';
    case EDatasetColumn.ADDITIONAL_CROPS:
      return 'additionalCrops';
    case EDatasetColumn.LAND_OWNERSHIP:
      return 'landOwnership';
    case EDatasetColumn.SITE_DESCRIPTION:
      return 'siteDescription';
    // Columns are only supported in mapping and not in the table
    case EDatasetColumn.LATITUDE:
    case EDatasetColumn.LONGITUDE:
    case EDatasetColumn.COORDINATES_LAT_LNG:
    case EDatasetColumn.COORDINATES_LNG_LAT:
    default:
      captureException('Unknown column mapping key in matchColumnMappingToColumnKey');
      return 'unknown_column_key';
  }
};

export const matchColumnKeyToColumnMapping: (
  key: string,
  settings?: IOriginTableSettings
) => TDatasetColumnType = (key, settings) => {
  switch (key) {
    case 'originClusterId':
      return EDatasetColumn.ORIGIN_CLUSTER_ID;
    case 'farmId':
      return EDatasetColumn.FARM_ID;
    case 'plotId':
      return EDatasetColumn.PLOT_ID;
    case 'plotName':
      return EDatasetColumn.PLOT_NAME;
    case 'geometry':
      return settings?.columns.geometry.format || EDatasetColumn.GEOMETRY_WKT;
    case 'plotSize':
      return settings?.columns.plotSize.format || EDatasetColumn.PLOT_SIZE_HA;
    case 'farmSize':
      return settings?.columns.farmSize.format || EDatasetColumn.FARM_SIZE_HA;
    case 'farmName':
      return EDatasetColumn.FARM_NAME;
    case 'farmOwnerFullName':
      return EDatasetColumn.FARM_OWNER_FULL_NAME;
    case 'farmOwnerFirstName':
      return EDatasetColumn.FARM_OWNER_FIRST_NAME;
    case 'farmOwnerLastName':
      return EDatasetColumn.FARM_OWNER_LAST_NAME;
    case 'numberOfFarmers':
      return EDatasetColumn.NUMBER_OF_FARMERS;
    case 'additionalCrops':
      return EDatasetColumn.ADDITIONAL_CROPS;
    case 'landOwnership':
      return EDatasetColumn.LAND_OWNERSHIP;
    case 'siteDescription':
      return EDatasetColumn.SITE_DESCRIPTION;
    default:
      captureException('Unknown column mapping key in matchColumnMappingToColumnKey');
      return EDatasetColumn.SITE_DESCRIPTION;
  }
};
